// One plain-English vocabulary for the whole app. Every place that shows an order status, a payment
// state, a term, an installment's timing or a timeline event reads from here — so a client never sees
// an internal code (PENDING_APPROVAL, LINK_SENT, "clock not started"…) and the same thing is always
// called the same name on every screen.
import { fmtMoney } from './matrix.js';

// --- Order status (what the order is doing), per audience -------------------------------------------
const ORDER_STATUS = {
  staff: { DRAFT: 'Draft', PENDING_APPROVAL: 'Awaiting approval', APPROVED: 'Approved', REJECTED: 'Rejected', CANCELLED: 'Cancelled' },
  customer: { DRAFT: 'Draft', PENDING_APPROVAL: 'Being reviewed', APPROVED: 'Confirmed', REJECTED: 'Not accepted', CANCELLED: 'Cancelled' },
};
export const orderStatusLabel = (status, role) => (role === 'INTERNAL_STAFF' ? ORDER_STATUS.staff : ORDER_STATUS.customer)[status] || String(status || '').replace(/_/g, ' ').toLowerCase();

// --- Payment state of a whole order ---------------------------------------------------------------
export const PAYMENT_STATUS = { UNPAID: 'Unpaid', DEPOSIT_PAID: 'Deposit paid', PART_PAID: 'Part-paid', PAID_IN_FULL: 'Paid' };
export const paymentStatusLabel = (s) => PAYMENT_STATUS[s] || (s ? String(s).replace(/_/g, ' ').toLowerCase() : '—');

// --- Payment terms ----------------------------------------------------------------------------------
export const TERMS = {
  PAY_IN_FULL: 'Pay in full before dispatch',
  DEPOSIT_BALANCE: 'Deposit now, balance on delivery',
  DEPOSIT_NET: 'Deposit now, balance on terms',
  NET_TERMS: 'Pay on terms',
  PAY_ON_DELIVERY: 'Pay on delivery',
  DEPOSIT_NET_FROM_DISPATCH: 'Deposit now, balance after dispatch',
  CUSTOM: 'Custom schedule',
};
export const termsLabel = (t) => TERMS[t] || (t ? String(t).replace(/_/g, ' ').toLowerCase() : '—');

// --- One installment's timing, in words -------------------------------------------------------------
const ANCHOR = { INVOICE: 'your invoice', DISPATCH: 'we ship', DELIVERY: 'delivery' };
const dateStr = (d) => new Date(d).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
export function installmentTiming(i) {
  if (i.status === 'PAID') return i.paidAt ? `Paid ${dateStr(i.paidAt)}` : 'Paid';
  if (i.status === 'VOID') return 'Cancelled';
  if (i.dueDate) {
    const overdue = new Date(i.dueDate).getTime() < Date.now();
    return `${overdue ? 'Overdue — was due' : 'Due'} ${dateStr(i.dueDate)}`;
  }
  if (i.dueAnchor) return `Due ${i.dueOffsetDays ?? 0} days after ${ANCHOR[i.dueAnchor] || 'your invoice'}`;
  if (i.trigger === 'ON_ARRIVAL') return i.activatedAt ? 'Due now (delivered)' : 'Due on delivery';
  return i.activatedAt ? 'Due now' : 'Due once confirmed';
}

export const PAYMENT_METHOD = {
  airwallex: 'Card / online',
  airwallex_card: 'Saved card',
  bank_transfer: 'Bank transfer',
  cheque: 'Cheque',
  cash: 'Cash',
  offline_card: 'Card (by phone)',
  other: 'Other',
};
export const paymentMethodLabel = (m) => PAYMENT_METHOD[m] || (m ? String(m).replace(/_/g, ' ') : '');

// --- Order progress (the stepper) -------------------------------------------------------------------
// Placed → Confirmed → Paid → Shipped → Delivered. "Paid" is reached when nothing more is owed now
// (full payment, or the deposit on deposit terms); ship-first terms show it after delivery instead.
export function orderProgress(order, payments) {
  if (order.status === 'REJECTED' || order.status === 'CANCELLED') return null;
  const shipped = Boolean(order.shippedAt) || ['FULFILLED', 'DELIVERED'].includes(order.fulfillmentState) || order.trackingNumbers?.length > 0;
  const delivered = order.fulfillmentState === 'DELIVERED';
  const confirmed = order.status === 'APPROVED';
  const ps = payments?.paymentStatus || order.paymentStatus;
  const paid = ps === 'PAID_IN_FULL' || ps === 'DEPOSIT_PAID';
  const steps = [
    { key: 'placed', label: 'Placed', done: true },
    { key: 'confirmed', label: 'Confirmed', done: confirmed },
    { key: 'paid', label: ps === 'DEPOSIT_PAID' ? 'Deposit paid' : 'Paid', done: paid },
    { key: 'shipped', label: 'Shipped', done: shipped },
    { key: 'delivered', label: 'Delivered', done: delivered },
  ];
  const current = steps.findIndex((s) => !s.done);
  return { steps, current: current === -1 ? steps.length : current };
}

// --- Timeline events --------------------------------------------------------------------------------
// label(meta, currency) → text. Staff see every event; clients/reps receive only customer-facing ones
// from the server (see orderService customerView). Unknown actions fall back to readable words.
const money = (m, cur) => (m?.amount != null ? fmtMoney(m.amount, m.currency || cur) : '');
const EVENTS = {
  created: () => 'Order placed',
  approval_requested: () => 'Sent for review',
  approved: () => 'Order confirmed',
  auto_approved: () => 'Approved automatically (trusted client)',
  rejected: (m) => `Order not accepted${m.note ? ` — ${m.note}` : ''}`,
  cancelled: (m) => `Order cancelled${m.note ? ` — ${m.note}` : ''}`,
  withdrawn: (m) => `Order withdrawn${m.reason ? ` — ${m.reason}` : ''}`,
  invoice_sent: (m) => `Invoice emailed${m.invoiceNumber ? ` (${m.invoiceNumber})` : ''}`,
  payment_recorded: (m, cur) => `Payment received ${money(m, cur)}${m.method ? ` · ${paymentMethodLabel(m.method)}` : ''}`.trim(),
  refund_issued: (m, cur) => `Refund issued ${money(m, cur)}`.trim(),
  stock_adjusted: (m) => `Items removed — out of stock${m.removed?.length ? ` (${m.removed.map((l) => `${l.quantity} × ${l.title}`).join(', ')})` : ''}`,
  tracking_synced: (m) => `Shipped${m.tracking?.length ? ` — tracking ${m.tracking.join(', ')}` : ''}`,
  delivered: () => 'Delivered',
  marked_arrived: () => 'Marked as delivered',
  order_edited: () => 'Order changed',
  po_updated: (m) => `PO number ${m.to ? `set to ${m.to}` : 'removed'}`,
  balance_due_date_set: (m) => `Balance due date set${m.dueDate ? ` — ${dateStr(m.dueDate)}` : ''}`,
  note: (m) => `Note: ${m.text || ''}`,
  reminder_sent: () => 'Payment reminder sent',
  dunning_sent: () => 'Overdue notice sent',
  credit_override: (m) => `Approved despite a credit hold${m.note ? ` — ${m.note}` : ''}`,
  // staff-only plumbing, in words
  schedule_set: () => 'Payment schedule set',
  payments_activated: () => 'Payment opened (invoice due)',
  link_sent: () => 'Payment link created',
  shopify_marked_paid: () => 'Sent to the warehouse (Shopify order created)',
  shopify_dispatch_failed: () => 'Could not send to the warehouse — needs attention',
  dispatch_deferred: () => 'Sending to the warehouse was retried',
  shopify_draft_deleted: () => 'Shopify draft removed',
  shipping_resolved: () => 'Shipping worked out',
  shipping_quoted: () => 'Shipping quoted',
  shipping_confirmed: () => 'Shipping confirmed',
  shipping_line_pushed: () => 'Shipping added to the Shopify order',
  shipping_destination_set: () => 'Destination changed',
  shipping_reconciled: () => 'Actual shipping cost recorded',
  schedule_rebase_skipped: () => 'Total changed after a payment — difference to bill separately',
  refund_failed: () => 'Refund FAILED — issue it in Airwallex',
  adjustment_failed: () => 'Out-of-stock adjustment failed',
  notified: (m) => `Client emailed (${m.event || 'update'})`,
  stalled_nudge: () => 'Follow-up reminder to the team',
  missing_shipping_credential: () => 'Missing customs ID — needs attention',
  missing_shipping_address: () => 'Missing shipping address — needs attention',
  archived: () => 'Archived',
  restored: () => 'Restored from archive',
  added_to_billing_batch: () => 'Added to a consolidated billing batch',
  removed_from_billing_batch: () => 'Removed from a consolidated billing batch',
};
export function eventLabel(a, currency) {
  const f = EVENTS[a.action];
  return f ? f(a.metadata || {}, currency) : String(a.action || '').replace(/_/g, ' ');
}
// Events that mean "something went wrong" — highlighted for staff.
export const PROBLEM_EVENTS = new Set(['shopify_dispatch_failed', 'refund_failed', 'adjustment_failed', 'missing_shipping_credential', 'missing_shipping_address', 'schedule_rebase_skipped']);
